import { useOktaAuth } from "@okta/okta-react";
import moment from "moment";
import { Button } from "react-blueprint-library";
import {
  updateContractStatus,
  getAddonStatus,
} from "../../../api/makeAPIRequest";
import {
  CONTRACT_STATUS_DESC,
  CONTRACT_STATUS_ID,
  CONTRACT_TYPE_DESCRIPTIONS,
  isBackgroundCheck,
  isPending,
  isSubmitted,
} from "../../../constants/Contract";
import "../../../assets/stylesheets/HomePage/HomePageActiveApps.scss";
import UploadDocument from "./UploadDocument";
import DocRequestWarning from "./UploadDocument/DocRequestWarning";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { buildAddOnText } from "../../../common/Functions";
import Loader from "../../SharedComponents/Loader";

const docCounterStyle = {
  justifyContent: "center",
  display: "flex",
  flexDirection: "column",
  color: "white",
  fontSize: "12px",
  fontFamily: "Lato",
  fontWeight: 700,
  lineHeight: "16px",
  wordWrap: "break-word",
  marginLeft: "10px",
  background: "#002e6b",
  width: "auto",
  height: "17px",
  padding: "0 8px",
  borderRadius: "25px",
  position: "relative",
  top: "3px",
};

const HomePageActiveApps = ({
  agencynumber,
  applicationStatus,
  contract,
  contracttype,
  contractid,
  mmlis,
  finance,
  lastchangedate,
  showGetStartedFor,
  cria,
  ia,
  setContractData,
  oktasubid,
  candidateDocStatusesLoading,
  onDocumentsUploaded = () => {},
  onShowConfirmationModal,
  hasSSN = false,
}) => {
  const {
    authState: {
      idToken: {
        claims: { sub, email },
      },
    },
  } = useOktaAuth();

  const [isDocRequest, setIsDocRequest] = useState(false);
  const [addonStatus, setAddonStatus] = useState(null);

  const uploadedDocs = useSelector(
    (state) => state.uploadedReqDocs?.docs || [],
  );
  const requestedDocs = useSelector(
    (state) => state.uploadedReqDocs?.reqdocs || [],
  );

  // Fetch addon status when component mounts if there are addons
  useEffect(() => {
    const fetchAddonStatus = async () => {
      if ((mmlis || finance || cria || ia) && oktasubid && contractid) {
        try {
          const status = await getAddonStatus(sub, contractid);
          setAddonStatus(status);
        } catch (error) {
          console.error("Error fetching addon status:", error);
        }
      }
    };

    fetchAddonStatus();
  }, [mmlis, finance, cria, ia, oktasubid, contractid, sub]);

  const hasReqDocsForContract = (contractId) => {
    if (!requestedDocs || !contractId) return false;
    return requestedDocs.some((doc) => {
      return (
        doc.contractid &&
        String(doc.contractid) === String(contractId) &&
        doc.requestStatus === "requested" &&
        !doc.guid
      );
    });
  };

  const getStarted = async () => {
    if (applicationStatus === CONTRACT_STATUS_DESC.INVITATION_SENT) {
      showGetStartedFor(contractid);
    } else {
      const shouldUpdateStatus =
        applicationStatus === CONTRACT_STATUS_DESC.RETURNED_TO_CANDIDATE ||
        applicationStatus === CONTRACT_STATUS_DESC.NEEDS_REVISION;
      if (shouldUpdateStatus) {
        await updateContractStatus(
          sub,
          contractid,
          email,
          CONTRACT_STATUS_ID.APPLICATION_IN_PROGRESS,
        );
      }
      // For some reason, the page changes location before the updateContractStatus Completes.
      // Set a timeout to fix this
      setTimeout(
        () => {
          window.location = `/startapplication?contract=${contractid}`;
        },
        shouldUpdateStatus ? 250 : 0,
      );
    }
  };

  const showAddOns = mmlis || finance || cria || ia;
  const addOnsText = buildAddOnText({
    mmlis,
    financingonly: finance,
    cria,
    ia,
  });

  const applicationStatusText =
    applicationStatus === CONTRACT_STATUS_DESC.INVITATION_SENT
      ? "Invitation sent on "
      : "Application Status as of ";

  const showStartApplicationButton =
    applicationStatus === CONTRACT_STATUS_DESC.APPLICATION_IN_PROGRESS ||
    applicationStatus === CONTRACT_STATUS_DESC.RETURNED_TO_CANDIDATE ||
    applicationStatus === CONTRACT_STATUS_DESC.NEEDS_REVISION ||
    applicationStatus === CONTRACT_STATUS_DESC.INVITATION_SENT;

  const startApplicationButtonText = showStartApplicationButton
    ? applicationStatus === CONTRACT_STATUS_DESC.RETURNED_TO_CANDIDATE ||
      applicationStatus === CONTRACT_STATUS_DESC.NEEDS_REVISION
      ? "Revise Application"
      : applicationStatus === CONTRACT_STATUS_DESC.INVITATION_SENT
        ? "Begin Application"
        : "Continue Application"
    : "Documents";

  const onViewRequestHandler = () => {
    setIsDocRequest(true);
  };

  const uploadedDocsCount = (uploadedDocs || []).filter(
    (doc) => doc.contractid === contractid,
  ).length;

  // Helper function to check if any addon status is pending (not approved)
  // Returns true if there are pending addons that should show "MassMutual Review"
  const hasAddonInMassMutualReview = () => {
    if (!addonStatus || !Array.isArray(addonStatus) || addonStatus.length === 0)
      return false;

    // Approved statuses - these should NOT trigger MassMutual Review
    const approvedStatuses = [
      CONTRACT_STATUS_DESC.MMLIS_APPROVED,
      CONTRACT_STATUS_DESC.FINANCING_APPROVED,
    ];

    // Check if any addon has a pending status (not an approved status)
    return addonStatus.some((addon) => {
      const status = addon.contractstatusdesc?.toLowerCase() || "";
      const isApproved = approvedStatuses.some(
        (s) => s.toLowerCase() === status || status.includes("approved"),
      );

      // Return true if status is pending (not approved)
      return (
        !isApproved &&
        (status.includes("pending") ||
          status.includes("review") ||
          addon.contractstatusdesc === CONTRACT_STATUS_DESC.MMLIS_Pending ||
          addon.contractstatusdesc === CONTRACT_STATUS_DESC.FINANCING_PENDING)
      );
    });
  };

  return (
    <>
      <div id={`activeApp${contractid}`} className="card--flat">
        <div className="card-body">
          {hasReqDocsForContract(contractid) ? (
            <DocRequestWarning onViewRequest={onViewRequestHandler} />
          ) : null}
          <div className="row">
            <div className="col-md-8 col-sm-12">
              <p className="eyebrow">
                {applicationStatus === CONTRACT_STATUS_DESC.INVITATION_SENT && (
                  <span className="badge badge-info">New</span>
                )}
                Agency #{agencynumber}
              </p>
              <div className="card-title-wrapper">
                <div className="card-title">{contracttype}</div>
                {showAddOns && <span className="add-ons">{addOnsText}</span>}
              </div>

              {(contracttype === CONTRACT_TYPE_DESCRIPTIONS.BROKER_CORP ||
                contracttype === CONTRACT_TYPE_DESCRIPTIONS.MEMBER_CORP) && (
                <p className="card-text">{contract?.corpname}</p>
              )}
              <p className="card-text">
                {applicationStatusText}
                {moment(lastchangedate).utc().format("MMMM DD, YYYY")}
              </p>

              {applicationStatus !== CONTRACT_STATUS_DESC.INVITATION_SENT && (
                <p className="card-text application-timeline">
                  {isBackgroundCheck(applicationStatus) ||
                  isPending(applicationStatus) ||
                  isSubmitted(applicationStatus) ||
                  (applicationStatus ===
                    CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE &&
                    hasAddonInMassMutualReview()) ? (
                    <span className="badge badge-primary--dark">Submitted</span>
                  ) : applicationStatus ===
                      CONTRACT_STATUS_DESC.RETURNED_TO_CANDIDATE ||
                    applicationStatus ===
                      CONTRACT_STATUS_DESC.NEEDS_REVISION ? (
                    <span className="badge badge-danger--dark">Returned</span>
                  ) : applicationStatus ===
                      CONTRACT_STATUS_DESC.APPLICATION_IN_PROGRESS ||
                    applicationStatus ===
                      CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE ? (
                    <span className="badge badge-success--dark">
                      In Progress
                    </span>
                  ) : (
                    <span className="badge badge-primary--dark">
                      In Progress
                    </span>
                  )}
                  <div className="strike-through" />
                  {applicationStatus === CONTRACT_STATUS_DESC.AGENCY_REVIEW ||
                  (applicationStatus ===
                    CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE &&
                    !hasAddonInMassMutualReview()) ||
                  applicationStatus ===
                    CONTRACT_STATUS_DESC.RETURNED_TO_AGENCY ||
                  applicationStatus ===
                    CONTRACT_STATUS_DESC.APPLICATION_SUBMITTED ||
                  isBackgroundCheck(applicationStatus) ? (
                    <span className="badge badge-success--dark">
                      Agency Review
                    </span>
                  ) : (
                    <span className="badge badge-primary--dark">
                      Agency Review
                    </span>
                  )}
                  <div className="strike-through" />
                  {applicationStatus ===
                    CONTRACT_STATUS_DESC.HOME_OFFICE_REVIEW ||
                  applicationStatus ===
                    CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE ||
                  applicationStatus ===
                    CONTRACT_STATUS_DESC.SUBMITTED_TO_HOME_OFFICE ||
                  applicationStatus === CONTRACT_STATUS_DESC.MMLIS_Pending ||
                  isPending(applicationStatus) ||
                  hasAddonInMassMutualReview() ? (
                    <span className="badge badge-success--dark">
                      MassMutual Review
                    </span>
                  ) : (
                    <span className="badge badge-primary--dark">
                      MassMutual Review
                    </span>
                  )}
                </p>
              )}
            </div>

            <div
              className="col-md-4 col-sm-12"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {uploadedDocsCount &&
              startApplicationButtonText !== "Documents" ? (
                <p style={{ padding: "0px, 16px, 0px, 16px" }}>
                  <Button
                    style={{ padding: "0 8px 0 8px" }}
                    onClick={() => {
                      setContractData({
                        contractid,
                        mmlis,
                        ia,
                        applicationStatus,
                        contracttypedesc: contracttype,
                        onlyCandidateDocs: true,
                      });
                    }}
                    variant="tertiary"
                  >
                    <div className="col-md-12" style={{ display: "flex" }}>
                      Documents
                      <div style={docCounterStyle}>{uploadedDocsCount}</div>
                    </div>
                  </Button>
                </p>
              ) : null}
              {applicationStatus !== CONTRACT_STATUS_DESC.INVITATION_SENT &&
              startApplicationButtonText !== "Documents" ? (
                <p style={{ padding: "0px, 16px, 0px, 16px" }}>
                  <UploadDocument
                    oktaSubId={oktasubid}
                    contractid={contractid}
                    setIsDocRequest={setIsDocRequest}
                    contracttype={contracttype}
                    isRequested={isDocRequest}
                    hasReqDocsForContract={hasReqDocsForContract(contractid)}
                    onDocumentsUploaded={onDocumentsUploaded}
                    startApplicationButtonText={startApplicationButtonText}
                    onShowConfirmationModal={onShowConfirmationModal}
                    hasSSN={hasSSN}
                  />
                  <br />
                </p>
              ) : null}
              {(showStartApplicationButton ||
                (contracttype === CONTRACT_TYPE_DESCRIPTIONS.FINANCING_ONLY &&
                  uploadedDocsCount) ||
                contracttype !== CONTRACT_TYPE_DESCRIPTIONS.FINANCING_ONLY) && (
                <p>
                  <Button
                    onClick={
                      showStartApplicationButton
                        ? getStarted
                        : () => {
                            setContractData({
                              contractid,
                              mmlis,
                              ia,
                              applicationStatus,
                              contracttypedesc: contracttype,
                              uploadedDocsCount,
                            });
                          }
                    }
                    variant={
                      startApplicationButtonText === "Documents"
                        ? "tertiary"
                        : showStartApplicationButton
                          ? "primary"
                          : "secondary"
                    }
                  >
                    {startApplicationButtonText === "Documents" ? (
                      <div className="col-md-12" style={{ display: "flex" }}>
                        {startApplicationButtonText}
                        <div style={docCounterStyle}>
                          {mmlis || ia
                            ? uploadedDocsCount + 4
                            : contracttype ===
                                CONTRACT_TYPE_DESCRIPTIONS.MEMBER_CORP
                              ? uploadedDocsCount + 2
                              : contracttype ===
                                  CONTRACT_TYPE_DESCRIPTIONS.FINANCING_ONLY
                                ? uploadedDocsCount
                                : uploadedDocsCount + 3}
                        </div>
                      </div>
                    ) : (
                      startApplicationButtonText
                    )}

                    {showStartApplicationButton && (
                      <span className="icon-caret-alt" />
                    )}
                  </Button>
                </p>
              )}

              {candidateDocStatusesLoading ? <Loader /> : null}

              {applicationStatus !== CONTRACT_STATUS_DESC.INVITATION_SENT &&
              startApplicationButtonText === "Documents" ? (
                <p style={{ padding: "0px, 16px, 0px, 16px" }}>
                  <UploadDocument
                    oktaSubId={oktasubid}
                    contractid={contractid}
                    setIsDocRequest={setIsDocRequest}
                    contracttype={contracttype}
                    isRequested={isDocRequest}
                    hasReqDocsForContract={hasReqDocsForContract(contractid)}
                    onDocumentsUploaded={onDocumentsUploaded}
                    startApplicationButtonText={startApplicationButtonText}
                    onShowConfirmationModal={onShowConfirmationModal}
                    hasSSN={hasSSN}
                  />
                  <br />
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePageActiveApps;
